<template>
  <AppCard 
    class="h-100 pa-5 border rounded-lg grid-item"
    :class="{
      'grid-item--selected': isSelected,
    }"
    elevation="0"
    :data-locator="itemType ? `${itemType}-card` : null"
  >
    <div class="d-flex flex-column justify-between h-100 row-gap-2">
      <div class="d-flex justify-space-between align-center column-gap-4">
        <div class="d-flex align-center">
          <v-checkbox
            :model-value="isSelected"
            data-locator="select-checkbox"
            hide-details
            @update:model-value="onUpdateSelection"
            v-if="isSelectable"
          />
        </div>
        <slot name="sub-header"></slot>
        <AppMenu
          :disabled="isLoading"
          :menu="menuItems"
          :data-locator="itemType ? `${itemType}s-item-menu` : null"
        >
          <template #activator="{ props }">
            <app-button type="icon" v-bind="props" data-locator="other-actions-menu-button">
              <app-icon-ds
                icon="ellipsis-h"
                weight="far"
                size="xs"
              />
            </app-button>
          </template>
        </AppMenu>
      </div>
      <template v-if="isLoading">
        <v-skeleton-loader type="text" class="mb-4" />
      </template>
      <template v-else>
        <h3
          ref="gridItemName"
          class="text-truncate mt-2 mb-4"
        >
          <AppTooltip :disabled="!hasTooltip">
            <template #activator="{ props }">
              <span v-bind="props" data-locator="name">
                {{ displayItemName }}
              </span>
            </template>
            <span>{{ displayItemName }}</span>
          </AppTooltip>
        </h3>
      </template>
      <div class="d-flex flex-column justify-between row-gap-4">
        <template v-if="isRendering || isLoading">
          <v-skeleton-loader type="image" />
        </template>
        <template v-else>
          <dl class="grid-item-details">
            <slot name="details"></slot>
          </dl>
        </template>
      </div>
      <template v-if="isRendering">
        <div class="d-flex mb-1 mt-auto">
          <div data-locator="rendering-text">
            {{ $t('common.AppGridItem.renderingText') }}
            <AppInfoTooltip>
              <span class="text--small">{{ $t('common.AppGridItem.renderingInfoTooltip') }}</span>
            </AppInfoTooltip>
          </div>
          <span class="ml-auto" data-locator="rendering-percentage">{{ displayRenderingPercentage }}</span>
        </div>
        <v-progress-linear
          :model-value="item.renderProgress"
          bg-color="grey-900"
          color="info-darken-1"
          height="6"
          rounded
          data-locator="rendering-bar"
        />
      </template>
      <template v-else-if="isLoading">
        <v-skeleton-loader type="button, button" />
      </template>
      <template v-else>
        <slot name="footer"></slot>
      </template>
    </div>
  </AppCard>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue'

import AppCard from '@/common/AppCard.vue'
import AppInfoTooltip from '@/common/AppInfoTooltip.vue'
import AppMenu from '@/common/AppMenu.vue'
import AppTooltip from '@/common/AppTooltip.vue'
import type { MenuItemType } from '@/types/menu.d.ts'

type GridItem = {
  name?: string
  renderProgress?: number
  [key: string]: any
}

export default defineComponent({
  name: 'AppGridItem',
  components: {
    AppCard,
    AppMenu,
    AppInfoTooltip,
    AppTooltip,
  },
  props: {
    item: {
      type: Object as PropType<GridItem>,
      default: () => ({}),
    },
    menuItems: {
      type: Array as PropType<MenuItemType[]>,
      default: () => [],
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    isRendering: {
      type: Boolean,
      default: false,
    },
    isSelectable: {
      type: Boolean,
      default: false,
    },
    isSelected: {
      type: Boolean,
      default: false,
    },
    itemType: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      hasTooltip: false,
    }
  },
  computed: {
    displayRenderingPercentage(): string {
      return this.item.renderProgress + '%' || ''
    },
    displayItemName(): string {
      return this.item?.name || ''
    },
  },
  methods: {
    onUpdateSelection(value: boolean | null): void {
      this.$emit('update:isSelected', value)
    },
  },
  async mounted(): Promise<void> {
    await this.$nextTick()
    
    const gridItemName = this.$refs['gridItemName'] as HTMLElement
    
    if (gridItemName) {
      this.hasTooltip = gridItemName.offsetWidth < gridItemName.scrollWidth
    } else {
      this.hasTooltip = true
    }
  },
})
</script>

<style scoped lang="scss">
@import "vuetify/settings";

.grid-item {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;

  &--selected {
    outline: 2px solid rgb(var(--v-theme-info-darken-1));
    background-color: rgb(var(--v-theme-info-lighten-5));

    :deep(.app-button) {
      background-color: transparent !important;
    }

    :deep(.app-button:disabled) {
      background-color: rgb(var(--v-theme-grey-50)) !important;
      outline-color: rgb(var(--v-theme-grey-200)) !important;
      color: rgb(var(--v-theme-grey-400)) !important;
    }
  }

  &-details {
    display: flex;
    flex-direction: column;
    list-style: none;
    padding: 0;
    row-gap: 2px;
  }
}

:deep(.v-checkbox .v-selection-control__wrapper) {
  width: auto;
  height: auto;
}

:deep(.v-skeleton-loader) {
  flex-wrap: nowrap;
  column-gap: 16px;
}

:deep(.v-skeleton-loader .v-skeleton-loader__text) {
  margin: 0;
  height: 20px;
}

:deep(.v-skeleton-loader .v-skeleton-loader__image) {
  height: 68px;
}

:deep(.v-skeleton-loader .v-skeleton-loader__button) {
  width: 45px;
  margin: 0;
}

:deep(.v-skeleton-loader .v-skeleton-loader__button:first-of-type) {
  max-width: none;
}
</style>
